<template>
  <div>
    <div v-show="items.length === 0 && !loading" v-text="$t('messages.noData')" />
    <div class="analysis-table">
      <table v-show="items.length !== 0">
        <thead>
          <tr>
            <template v-for="(head, headIndex) in crossSearchFields">
              <th v-if="headIndex === 0" :key="head.name" ref="searchFields" class="fixed">
                {{ head.name }}
              </th>
              <th v-else :key="head.name" class="fixed">
                {{ head.name }}
              </th>
            </template>
            <th />
            <th v-for="head in staticHeads" :key="head.name">
              <button
                class="p-table__content__sort"
                :class="orderCss(head.id)"
                @click="sortTables(head.id)"
              >
                {{ head.name }}
              </button>
            </th>
          </tr>
          <tr class="p-table__content__space">
            <td colspan="5" />
          </tr>
        </thead>
        <tbody v-show="!loading">
          <tr v-if="headItem" class="head-item">
            <template v-for="(field, fieldIndex) in headItem.fields" :key="fieldIndex">
              <td class="table-head fixed">
                <div class="table-head__content">
                  <p>
                    <span v-if="fieldIndex === 0" class="index-number">1. </span>
                    {{ field.name }}
                  </p>
                  <button v-if="fieldIndex === headItem.fields.length - 1 && headItem.isDataEnough" @click="$emit('detailIconClicked', headItem)">
                    <Icon name="iconDetailArrow" class="table-head__icon" />
                  </button>
                </div>
              </td>
            </template>
            <td>
              <div class="radarChart">
                <ChartRadar
                  :chart-data="chartData(headItem)"
                  :options="chartOptions"
                />
              </div>
            </td>
            <td v-if="!headItem.isDataEnough" class="dataNotEnough" colspan="4">
              {{ $t('pages.companies.analysis.qscas.dataNotEnough') }}
            </td>
            <template v-else>
              <td v-for="head in staticHeads" :key="head.id" class="u-taR">
                <div class="p-table__color" :class="panelColorClass(headItem, head.id)">
                  <div>
                    {{ headItem[head.id] !== null ? numberLocaleString(headItem[head.id]) : '-' }}
                  </div>
                </div>
                <span>{{ head.text }}</span>
              </td>
            </template>
          </tr>
          <template v-for="(item, itemIndex) in itemList" :key="itemIndex">
            <tr v-show="!hideNoData || item.isDataEnough">
              <template v-for="(field, fieldIndex) in item.fields" :key="fieldIndex">
                <td class="table-head fixed">
                  <div class="table-head__content">
                    <p>
                      <template v-if="fieldIndex === 0">
                        <span class="index-number">{{ itemIndex + 2 + '. ' }}</span>
                        <a v-if="item.isDataEnough" class="storeName" @click.prevent="changeIndividualAnalysisField(field)">
                          {{ field.name }}
                        </a>
                        <template v-else>
                          {{ field.name }}
                        </template>
                      </template>
                      <span v-else>{{ field.name }}</span>
                      <StoresCount v-if="!!item.stores && fieldIndex === item.fields.length - 1" :store-names="getStoreNames(item.stores)" :is-omit-single-store="isStoreAnalysisAxis" @open-modal="openModal" />
                    </p>
                    <button v-if="fieldIndex === item.fields.length - 1 && item.isDataEnough" @click="$emit('detailIconClicked', item)">
                      <Icon name="iconDetailArrow" class="table-head__icon" />
                    </button>
                  </div>
                </td>
              </template>
              <td>
                <div class="radarChart">
                  <ChartRadar
                    :chart-data="chartData(item)"
                    :options="chartOptions"
                  />
                </div>
              </td>
              <td v-if="!item.isDataEnough" class="dataNotEnough" colspan="4">
                {{ $t('pages.companies.analysis.qscas.dataNotEnough') }}
              </td>
              <td v-for="head in staticHeads" v-else :key="head.id" class="u-taR">
                <div class="p-table__color" :class="panelColorClass(item, head.id)">
                  <button v-if="isActiveSentencesModal" type="button" class="analysis-table__score" @click="onClickScore(item.stores, head.id)">
                    {{ item[head.id] !== null ? numberLocaleString(item[head.id]) : '-' }}
                  </button>
                  <template v-else>
                    {{ item[head.id] !== null ? numberLocaleString(item[head.id]) : '-' }}
                  </template>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <Loading v-show="loading" />
    <StoresModal v-if="modal" :store-names="storeNames" @close="closeModal" />
  </div>
</template>

<script>
import Loading from '../../Loading'
import StoresModal from '../StoresModal'
import StoresCount from '../StoresCount'
import { toNumberLocaleString } from '../../../modules/localeString'
import ChartRadar from '../../ChartRadar'
import Icon from '../../Icon'
import { calcUnit, calcUnits, evalPanelColorClassNumber } from '../../../modules/colorTip'
import { LOCALONE_URLS } from '../../../modules/urls'

export default {
  name: 'CompaniesAnalysisQscasTable',
  components: {
    Icon,
    Loading,
    StoresModal,
    StoresCount,
    ChartRadar
  },
  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    crossSearchFields: {
      type: Array,
      required: true,
    },
    staticHeads: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    truncateStoreName: {
      type: Boolean,
      default: false,
    },
    hideNoData: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'detailIconClicked',
    'change-individual-analysis-field',
    'clickScore'
  ],
  data() {
    return {
      modal: false,
      storeNames: null,
      sortKey: '',
      sortOrder: '',
    }
  },
  computed: {
    companyUuid() {
      return this.$route.params.company_uuid
    },
    headItem() {
      return this.items[0]
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          r: {
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              stepSize: 50
            },
          },
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          },
        },
      }
    },
    itemList() {
      let items = this.items.slice(1)

      switch (this.sortOrder) {
      case 'asc':
        items = items.sort((a, b) => {
          if (a[this.sortKey] < b[this.sortKey]) return -1
          if (a[this.sortKey] > b[this.sortKey]) return 1
          return 0
        })
        break
      case 'desc':
        items = items.sort((a, b) => {
          if (a[this.sortKey] > b[this.sortKey]) return -1
          if (a[this.sortKey] < b[this.sortKey]) return 1
          return 0
        })
        break
      default: // 最初は分析軸の名前でソート
        if (!items.length) break

        // クロス分析の場合、セカンダリーからソートさせるためソート順の配列を用意して外側から順にソート
        var sortIndexOrderList = items[0].fields.map((_, i) => i).reverse()
        sortIndexOrderList.forEach((i) => {
          items = items.sort((a, b) => {
            // 未設定(id=-1)は一番下になるよう判定
            if (Number(a.fields[i].id) === -1) return 1
            if (Number(b.fields[i].id) === -1) return -1

            if (a.fields[i].name < b.fields[i].name) return -1
            if (a.fields[i].name > b.fields[i].name) return 1
            return 0
          })
        })
      }

      return items
    },
    /**
     * カラーチップの色判定のために使う
     * @return {{ quality: CalculatedObj, service: CalculatedObj, cleanliness: CalculatedObj, atmosphere: CalculatedObj }}
     */
    minMaxDiffUnit() {
      let unit = {}
      let values = {}
      const keys = this.staticHeads.map(type => type.id)
      keys.forEach(type => {
        unit[type] = {
          unit: [],
          min: 0,
          max: 0,
        }
        values[type] = []
      })

      this.items.filter(v => v.isDataEnough).forEach(qsca => {
        keys.forEach(type => {
          if (qsca[type] !== null) {
            values[type].push(qsca[type])
          }
        })
      })
      keys.forEach(type => {
        const max = Math.max(...values[type])
        const min = Math.min(...values[type])
        unit[type]['unit'] = calcUnit(max,min)
        unit[type]['min'] = min
        unit[type]['max'] = max
      })
      return unit
    },
    /**
     * パフォーマンス最適化のためにcomputedに
     * @return {{ quality: MultiUnitCalculatedObj, service: MultiUnitCalculatedObj, cleanliness: MultiUnitCalculatedObj, atmosphere: MultiUnitCalculatedObj }}
     */
    panelColorClassCondition() {
      let obj = {}
      for (const [key, value] of Object.entries(this.minMaxDiffUnit)) {
        obj[key] = {
          units: [],
          min: 0,
          max: 0,
        }
        obj[key]['units'] = calcUnits(value)
        obj[key]['min'] = value.min
        obj[key]['max'] = value.max
      }
      return obj
    },
    isStoreAnalysisAxis() {
      return this.crossSearchFields && this.crossSearchFields.filter((field) => field.id === 'store').length !== 0
    },
    isActiveSentencesModal() {
      return !LOCALONE_URLS.some(url => location.hostname.includes(url))
    }
  },
  methods: {
    panelColorClass(item, id) {
      return item[id] !== null ? 'p-table__color--' + this.evalPanelColorClass(item, id) : ''
    },
    changeIndividualAnalysisField(field) {
      this.$emit('change-individual-analysis-field', field)
    },
    evalPanelColorClass(item, id) {
      // 全て同じ値だったら、真ん中の色を返す
      return evalPanelColorClassNumber(this.panelColorClassCondition[id], item[id])
    },
    chartData(item) {
      return {
        labels: this.staticHeads.map(v => v.name),
        datasets: [
          {
            label: item.fields.map(v => v.name).join('/'),
            data: item.isDataEnough
              ? this.staticHeads.map(v => item[v.id])
              : new Array(this.staticHeads.length).fill(0),
            borderColor: '#E9446A',
            backgroundColor: 'transparent'
          }
        ]
      }
    },
    /**
     * 数値の3桁区切り
     * @param  { Number } value
     * @return { Number }
     */
    numberLocaleString(value) {
      return toNumberLocaleString(value)
    },
    openModal(storeNames) {
      this.storeNames = storeNames
      this.modal = true
    },
    closeModal() {
      this.storeNames = null
      this.modal = false
    },
    sortTables(sortKey) {
      if (this.sortKey === sortKey) { // key が一致する場合は2回目押下時
        this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      } else { // それ以外の場合は key 1回目押下時
        this.sortKey = sortKey
        this.sortOrder = 'desc'
      }
    },
    orderCss(key) {
      if (!this.sortOrder) return '' // 初期値の場合は CSS なし
      if (this.sortKey === key) { // 一致する key の場合に CSS がつく
        return this.sortOrder === 'asc' ? 'is-asc' : 'is-desc'
      } else { // それ以外の key の場合は CSS なし
        return ''
      }
    },
    getStoreNames(stores) {
      return stores.map((store) => store.name)
    },
    onClickScore(stores, id) {
      const storeUuids = stores.map((store) => store.uuid)
      this.$emit('clickScore', { category: id.toUpperCase(), uuids: storeUuids })
    },
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../../stylesheets/biz_spa/foundation/_var.scss';

.analysis-table {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 84vh;
  table {
    position: relative;
    z-index: 0;
  }
  thead {
    position: sticky;
    top: 0;
    z-index: 2;
  }
  &__score {
    text-decoration: underline;
  }
}

.head-item {
  .u-taR {
    background-color: #F8F9FA;
    font-size: 18px;
    font-weight: bold;

    span {
      display: block;
      font-size: 10px;
      font-weight: normal;
    }
  }
}

tbody .table-head {
  background-color: #F8F9FA;
  border-right: 1px solid #E4E7EB;
}

.fixed {
  position: sticky;
  left: 0;
  z-index: 1;
  @media screen and (max-width: 768px) {
    position: initial;
  }
}

.index-number {
  font-weight: bold;
  margin: 0 8px;
}

.radarChart {
  width: 180px;
  margin-left: auto;
  margin-right: auto;
}
.table-head {
  &__content {
    display: flex;
    justify-content: space-between;
  }
  &__icon {
    vertical-align: middle;
  }
}
.colorPanel {
  width: 30px;
  height: 30px;
  border-radius: 2px;
  &__gray {
    background: #D2D5DB;
  }
  &__red {
    background: $color-accent-primary;
  }
  &__green {
    background: $complete-color;
  }
}
.storeName {
  cursor: pointer;
}
.dataNotEnough {
  color: #6D7381;
  text-align: center;
  font-size: 12px;
}
</style>
